import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, Loader2, ShieldCheck } from "lucide-react";
import { api, apiError, AVAILABLE_FOR_LABELS, COMM_LABELS, PROGRAM_LABELS } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { HEYCATCH_EVENTS, trackOutcomeOnce } from "@/lib/heycatch";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const PROGRAMS = ["AA", "NA", "CMA", "SMART", "MULTIPLE", "OTHER"];
const STATUSES = [
  { value: "accepting", label: "Accepting New Sponsees", dot: "#1F7A4D" },
  { value: "limited", label: "Limited Availability", dot: "#B7791F" },
  { value: "not_accepting", label: "Not Accepting New Sponsees", dot: "#B42318" },
];

export default function BecomeSponsor() {
  const { user, sponsorProfile, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    recovery_program: "",
    years_in_recovery: "",
    years_sponsoring: "",
    about_me: "",
    sponsorship_style: "",
    communication_methods: ["messaging"],
    available_for: ["messaging", "general_support"],
    availability_status: "accepting",
    language: "English",
    city: "",
    state: "",
    country: "",
  });

  useEffect(() => {
    if (sponsorProfile) {
      setForm({
        recovery_program: sponsorProfile.recovery_program || "",
        years_in_recovery: sponsorProfile.years_in_recovery ?? "",
        years_sponsoring: sponsorProfile.years_sponsoring ?? "",
        about_me: sponsorProfile.about_me || "",
        sponsorship_style: sponsorProfile.sponsorship_style || "",
        communication_methods: sponsorProfile.communication_methods || ["messaging"],
        available_for: sponsorProfile.available_for || [],
        availability_status: sponsorProfile.availability_status || "accepting",
        language: sponsorProfile.language || "English",
        city: sponsorProfile.location?.city || "",
        state: sponsorProfile.location?.state || "",
        country: sponsorProfile.location?.country || "",
      });
    } else if (user) {
      setForm((f) => ({
        ...f,
        recovery_program: user.recovery_program || "",
        city: user.location?.city || "",
        state: user.location?.state || "",
        country: user.location?.country || "",
      }));
    }
  }, [sponsorProfile, user]);

  const toggleArr = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/register?type=sponsor&next=/become-a-sponsor");
      return;
    }
    if (!form.recovery_program) {
      toast.error("Please choose the fellowship or pathway you participate in.");
      return;
    }
    if (form.about_me.trim().length < 10) {
      toast.error("Please write a short introduction (at least 10 characters).");
      return;
    }
    setBusy(true);
    try {
      // Captured before the save: `sponsorProfile` is null only until the very
      // first publish, which is how we tell a new profile from a later edit.
      const isFirstPublish = !sponsorProfile;
      await api.put("/sponsor-profile", {
        recovery_program: form.recovery_program,
        years_in_recovery: form.years_in_recovery === "" ? null : Number(form.years_in_recovery),
        years_sponsoring: form.years_sponsoring === "" ? null : Number(form.years_sponsoring),
        about_me: form.about_me,
        sponsorship_style: form.sponsorship_style || null,
        communication_methods: form.communication_methods,
        available_for: form.available_for,
        accepting_sponsees: form.availability_status !== "not_accepting",
        availability_status: form.availability_status,
        language: form.language,
        location: { city: form.city || null, state: form.state || null, country: form.country || null },
      });
      await refresh();
      trackEvent("sponsor_profile_completed", {
        account_type: "sponsor",
        user_id: user?.id || null,
      });
      // HeyCatch: only the first time a sponsor publishes. Editing an existing
      // profile is not a new conversion, and the user-id guard means a refresh
      // cannot report it twice either.
      if (isFirstPublish) {
        trackOutcomeOnce(
          `sponsor_profile:${user?.id}`,
          HEYCATCH_EVENTS.SPONSOR_PROFILE_COMPLETED
        );
      }
      toast.success("Your sponsor profile is live. Sponsor accounts are free forever.");
      navigate("/sponsor-dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="rounded-2xl bg-[#1F2A2E] text-[#F7F4EF] p-6">
        <HeartHandshake className="w-7 h-7 text-[#D07A4A]" />
        <h1 className="mt-3 font-display text-3xl font-bold">
          {sponsorProfile ? "Edit your sponsor profile" : "Become a Sponsor"}
        </h1>
        <p data-testid="become-sponsor-definition" className="mt-2 text-sm leading-6 text-[#E6EFF0] font-medium">
          Becoming a sponsor on Sponsor Me means volunteering your lived experience to support
          another person in recovery.
        </p>
        <p className="mt-2 text-sm leading-6 text-[#CBD6D8]">
          Sponsor accounts are completely free, with unlimited messaging. You choose whether you are
          accepting new sponsees. You can pause your profile at any time.
        </p>
      </div>

      {!user && (
        <div className="mt-5 sm-card p-5 bg-white">
          <p className="text-sm text-[#4B5A60]">
            Create a free account first, then set up your sponsor profile.
          </p>
          <Button
            onClick={() => navigate("/register?type=sponsor&next=/become-a-sponsor")}
            data-testid="sponsor-signup-cta"
            className="mt-3 bg-[#1F6F78] hover:bg-[#185B62] text-white"
          >
            Create free sponsor account
          </Button>
        </div>
      )}

      <form onSubmit={submit} className="mt-5 space-y-5">
        <div className="sm-card p-6 bg-white">
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            Which fellowship or pathway do you participate in?
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {PROGRAMS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setForm({ ...form, recovery_program: p })}
                data-testid={`sponsor-program-${p}`}
                className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
                  form.recovery_program === p
                    ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                    : "border-[#E3DED4] text-[#4B5A60] hover:border-[#BFD9DB]"
                }`}
              >
                {PROGRAM_LABELS[p]}
              </button>
            ))}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                Years in recovery (optional)
              </label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={form.years_in_recovery}
                onChange={(e) => setForm({ ...form, years_in_recovery: e.target.value })}
                data-testid="years-in-recovery-input"
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                Years sponsoring (optional)
              </label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={form.years_sponsoring}
                onChange={(e) => setForm({ ...form, years_sponsoring: e.target.value })}
                data-testid="years-sponsoring-input"
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-[#7A868B]">
            Recovery time is optional. You never need to share a sobriety date.
          </p>
        </div>

        <div className="sm-card p-6 bg-white space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
              About Me
            </label>
            <Textarea
              value={form.about_me}
              onChange={(e) => setForm({ ...form, about_me: e.target.value })}
              rows={5}
              data-testid="about-me-input"
              placeholder="Introduce yourself in a few sentences. What has your recovery looked like, and what do you enjoy about helping others?"
              className="mt-1.5 bg-white border-[#E3DED4]"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
              My Approach (optional)
            </label>
            <Textarea
              value={form.sponsorship_style}
              onChange={(e) => setForm({ ...form, sponsorship_style: e.target.value })}
              rows={3}
              data-testid="approach-input"
              placeholder="e.g. I believe sponsorship should be honest, supportive and centered around working a recovery program."
              className="mt-1.5 bg-white border-[#E3DED4]"
            />
          </div>
        </div>

        <div className="sm-card p-6 bg-white space-y-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
              How do you prefer to communicate?
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(COMM_LABELS).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleArr("communication_methods", v)}
                  data-testid={`comm-${v}`}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
                    form.communication_methods.includes(v)
                      ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                      : "border-[#E3DED4] text-[#4B5A60]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-[#7A868B]">
              Your phone number is never displayed publicly.
            </p>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
              Available For
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(AVAILABLE_FOR_LABELS).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => toggleArr("available_for", v)}
                  data-testid={`available-${v}`}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
                    form.available_for.includes(v)
                      ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                      : "border-[#E3DED4] text-[#4B5A60]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
              I Am Currently
            </label>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setForm({ ...form, availability_status: s.value })}
                  data-testid={`availability-${s.value}`}
                  className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold bg-white ${
                    form.availability_status === s.value
                      ? "border-[#1F6F78] bg-[#F2FAFA] text-[#1F2A2E]"
                      : "border-[#E3DED4] text-[#4B5A60]"
                  }`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.dot }} />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">City</label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">State</label>
              <Input
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">Country</label>
              <Input
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
                Language
              </label>
              <Input
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                className="mt-1.5 h-11 bg-white border-[#E3DED4]"
              />
            </div>
          </div>
          <p className="flex items-start gap-2 text-xs text-[#7A868B] leading-5">
            <ShieldCheck className="w-4 h-4 shrink-0 text-[#1F6F78]" />
            We never publish your exact address. Only city, state and country appear on your profile.
          </p>
        </div>

        <Button
          type="submit"
          disabled={busy}
          data-testid="save-sponsor-profile-button"
          className="w-full h-12 bg-[#1F6F78] hover:bg-[#185B62] text-white text-base"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : sponsorProfile ? (
            "Save changes"
          ) : (
            "Publish my sponsor profile"
          )}
        </Button>
      </form>
    </div>
  );
}
